import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  CircularProgress,
  LinearProgress,
  Tabs,
  Tab,
  Button,
  Menu,
  MenuItem,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { useLanguage } from '../../utils/i18n';

const formatDurationSec = (totalSeconds) => {
  if (!totalSeconds || totalSeconds < 0) return '00:00:00';
  const h = Math.floor(totalSeconds / 3600)
    .toString()
    .padStart(2, '0');
  const m = Math.floor((totalSeconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const VisitationTable = ({ data }) => {
  const { t } = useLanguage();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => new Date(a.in_time).getTime() - new Date(b.in_time).getTime());
  }, [data]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>{t('time.start', 'Start')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>{t('time.end', 'End')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>{t('reports.duration', 'Duration')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>{t('reportModule.distanceKm', 'Distance (km)')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>{t('general.location', 'Location')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>{t('reportModule.startOdo', 'Start Odo')}</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>{t('reportModule.endOdo', 'End Odo')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, idx) => (
              <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={{ py: 1 }}>{row.in_time ? dayjs(row.in_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</TableCell>
                <TableCell sx={{ py: 1 }}>{row.out_time ? dayjs(row.out_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</TableCell>
                <TableCell sx={{ py: 1 }}>{formatDurationSec(row.duration || 0)}</TableCell>
                <TableCell sx={{ py: 1 }}>{((row.distance || 0) / 1000).toFixed(2)}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <a
                    href={row.location_direct || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#3b82f6', textDecoration: 'none' }}
                  >
                    {row.spatial_name || row.street || '-'}
                  </a>
                </TableCell>
                <TableCell sx={{ py: 1 }}>{row.start_odo != null ? (row.start_odo / 1000).toFixed(2) : '-'}</TableCell>
                <TableCell sx={{ py: 1 }}>{row.end_odo != null ? (row.end_odo / 1000).toFixed(2) : '-'}</TableCell>
              </TableRow>
            ))}
            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                  {t('reportModule.noDataAvailable', 'No data available')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 100]}
        component="div"
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  const options = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
  return d.toLocaleDateString('en-GB', options).replace(',', '');
};

const RitaseDetailList = ({ details, nextRitaseTitle }) => {
  const { t } = useLanguage();

  if (!details || details.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: '#f8fafc', textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('reports.noStopData', 'There is no stop data for this trip.')}
        </Typography>
      </Paper>
    );
  }

  const renderDetailCard = (detail, isTrip = false, isLast = false, hasNextTrip = false) => (
    <Box sx={{ display: 'flex', position: 'relative', mb: isLast ? 0 : 2 }}>
      {/* Vertical line connecting to next item */}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            top: 36,
            bottom: -16, // connects through the margin-bottom (16px)
            left: 17, // center of 36px icon (18px - 1px for 2px width line)
            width: 2,
            borderLeft: isTrip || hasNextTrip ? '2px dashed #94a3b8' : '2px solid #cbd5e1',
            zIndex: 0
          }}
        />
      )}

      {/* Horizontal line for Trip connecting icon to card */}
      {isTrip && (
        <Box
          sx={{
            position: 'absolute',
            top: 17, // center of the 36px icon
            left: 36, // right edge of the icon
            width: 32, // space to the card (since pl is 4 = 32px)
            borderTop: '2px dashed #94a3b8',
            zIndex: 0
          }}
        />
      )}

      {/* Left Icon (Timeline Node) */}
      <Box sx={{ mr: 2, zIndex: 1 }}>
        {isTrip ? (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: '#ffffff',
              color: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #94a3b8',
              boxShadow: '0 0 0 4px #ffffff'
            }}
          >
            <LocalShippingIcon fontSize="small" />
          </Box>
        ) : (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)'
            }}
          >
            {detail.step}
          </Box>
        )}
      </Box>

      {/* Content Card */}
      <Box sx={{ flex: 1, pl: isTrip ? 4 : 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: isTrip ? '#f8fafc' : '#ffffff',
            border: isTrip ? '1px dashed #94a3b8' : '1px solid #e2e8f0',
            position: 'relative'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#0f172a' }}>
                {detail.locationName}
              </Typography>
              {!isTrip && (
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  {t('reports.stop', 'Stop')} {detail.step} {t('reports.of', 'of')} {details.length}
                </Typography>
              )}
              {isTrip && (
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  {t('reportModule.tripInformation', 'Trip Information')}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  bgcolor: isTrip ? '#ffffff' : '#f1f5f9',
                  color: '#0f172a',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textAlign: 'center',
                  minWidth: 80,
                  border: isTrip ? '1px solid #e2e8f0' : 'none'
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', display: 'block', mb: 0.5 }}>
                  {t('reports.distanceCap', 'DISTANCE')}
                </Typography>
                <Typography variant="body2" fontWeight="800">
                  {detail.distance}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: isTrip ? '#ffffff' : '#f1f5f9',
                  color: '#0f172a',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textAlign: 'center',
                  minWidth: 80,
                  border: isTrip ? '1px solid #e2e8f0' : 'none'
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', display: 'block', mb: 0.5 }}>
                  {t('reports.durationCap', 'DURATION')}
                </Typography>
                <Typography variant="body2" fontWeight="800">
                  {detail.duration}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item size={{ xs: 6 }}>
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: isTrip ? '#ffffff' : '#f8fafc', height: '100%' }}>
                <Typography variant="caption" fontWeight="800" sx={{ color: '#1e40af', display: 'block', mb: 1.5, letterSpacing: 0.5 }}>
                  {t('reports.startInfo', 'START INFO')}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item size={{ xs: 4 }}>
                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                      {t('reports.time', 'Time')}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 8 }}>
                    <Typography variant="body2" fontWeight="500">
                      {detail.start.time}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 4 }}>
                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                      {t('reports.odometer', 'Odometer')}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 8 }}>
                    <Typography variant="body2" fontWeight="500">
                      {detail.start.odo}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item size={{ xs: 6 }}>
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: isTrip ? '#ffffff' : '#f8fafc', height: '100%' }}>
                <Typography variant="caption" fontWeight="800" sx={{ color: '#1e40af', display: 'block', mb: 1.5, letterSpacing: 0.5 }}>
                  {t('reports.endInfo', 'END INFO')}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item size={{ xs: 4 }}>
                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                      {t('reports.time', 'Time')}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 8 }}>
                    <Typography variant="body2" fontWeight="500">
                      {detail.end.time}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 4 }}>
                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                      {t('reports.odometer', 'Odometer')}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 8 }}>
                    <Typography variant="body2" fontWeight="500">
                      {detail.end.odo}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              p: 1.5,
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: isTrip ? '#ffffff' : 'transparent'
            }}
          >
            <Box sx={{ flex: 1, textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', mb: 0.5 }}>
                {t('reports.engineOnCap', 'ENGINE ON')}
              </Typography>
              <Typography variant="body2" fontWeight="700" color="#0f172a">
                {detail.activity.engineOn}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', mb: 0.5 }}>
                {t('reports.engineOffCap', 'ENGINE OFF')}
              </Typography>
              <Typography variant="body2" fontWeight="700" color="#0f172a">
                {detail.activity.engineOff}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', mb: 0.5 }}>
                {t('reports.idleCap', 'IDLE')}
              </Typography>
              <Typography variant="body2" fontWeight="700" color="#0f172a">
                {detail.activity.idle}
              </Typography>
            </Box>
          </Box>

          {isTrip && detail.visitation && detail.visitation.length > 0 && (
            <Accordion sx={{ mt: 2, border: '1px solid #e2e8f0', boxShadow: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<KeyboardArrowDownIcon />}
                sx={{ bgcolor: '#f8fafc', minHeight: 36, '& .MuiAccordionSummary-content': { my: 1 } }}
              >
                <Typography variant="body2" fontWeight="600" color="#3b82f6">
                  {t('reportModule.viewDetailVisitation', 'View Detail Visitation')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <VisitationTable data={detail.visitation} />
              </AccordionDetails>
            </Accordion>
          )}
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxHeight: '600px', overflowY: 'auto', p: 1 }}>
      {details.map((detail, idx) => {
        const hasNextTrip = !!detail.tripInfo;
        const isLastDetail = idx === details.length - 1;
        let modifiedTripInfo = null;
        if (detail.tripInfo) {
          modifiedTripInfo = { ...detail.tripInfo };
          if (isLastDetail && nextRitaseTitle) {
            modifiedTripInfo.locationName = `${detail.locationName} ~ ${nextRitaseTitle}`;
          }
        }

        return (
          <Box key={idx}>
            {renderDetailCard(detail, false, isLastDetail && !hasNextTrip, hasNextTrip)}
            {modifiedTripInfo && renderDetailCard(modifiedTripInfo, true, isLastDetail, false)}
          </Box>
        );
      })}
    </Box>
  );
};
const RitaseAccordionItem = ({ ritase, defaultExpanded, nextRitaseTitle }) => {
  const { t } = useLanguage();
  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        mb: 1,
        borderRadius: '8px !important',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
        '&:before': { display: 'none' },
        overflow: 'hidden'
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ color: '#64748b' }} />}
        sx={{
          bgcolor: '#f8fafc',
          '& .MuiAccordionSummary-content': { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', textTransform: 'uppercase' }}
          >
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6', mr: 1.5 }} />
            {ritase.title}
          </Typography>
          <Tooltip
            title={
              ritase.isActiveRoute ? t('achievement.activeRoute', 'Active Route') : t('achievement.historicalRoute', 'Historical Route')
            }
            placement="top"
          >
            <Chip
              label={
                ritase.isActiveRoute ? t('achievement.activeRoute', 'Active Route') : t('achievement.historicalRoute', 'Historical Route')
              }
              size="small"
              sx={{
                ml: 1.5,
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 'bold',
                bgcolor: ritase.isActiveRoute ? '#485D9A' : '#9E9E9E',
                color: '#fff'
              }}
            />
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 4, mr: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
              {t('reports.distance', 'Distance')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {ritase.totalDistance}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
              {t('reports.duration', 'Duration')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {ritase.totalDuration}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
              {t('reports.stops', 'Stops')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {ritase.stops}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
        <RitaseDetailList details={ritase.details} nextRitaseTitle={nextRitaseTitle} />
      </AccordionDetails>
    </Accordion>
  );
};

const VehicleAccordionItem = ({ vehicleName, vehicleData }) => {
  const { t } = useLanguage();
  const totalAllRetase = vehicleData.ritases.length;

  return (
    <Accordion
      disableGutters
      defaultExpanded
      sx={{
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        '&:before': { display: 'none' },
        mb: 3,
        borderRadius: '16px !important',
        overflow: 'hidden'
      }}
    >
      <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} sx={{ bgcolor: '#ffffff', minHeight: '64px', px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: 'rgba(72, 93, 154, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LocalShippingIcon sx={{ color: '#485D9A' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="700" sx={{ color: '#1e293b' }}>
              {vehicleName}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
              {t('reportModule.totalRitase', 'Total Ritase')}
            </Typography>
            <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#485D9A' }}>
              {totalAllRetase} {t('reports.ritase', 'Ritase')}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        {vehicleData.ritases.map((ritase, idx) => {
          let computedNextRitaseTitle = null;
          if (idx < vehicleData.ritases.length - 1) {
            computedNextRitaseTitle = vehicleData.ritases[idx + 1].title;
          }

          return (
            <RitaseAccordionItem key={ritase.id} ritase={ritase} defaultExpanded={idx === 0} nextRitaseTitle={computedNextRitaseTitle} />
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
};

const RitaseDetailReportSort = ({ data = [], loading = false, loadingProgress = 0, loadingText = '', fromDate, toDate }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  const dateTabs = useMemo(() => {
    if (!fromDate || !toDate) return [];
    const dates = [
      {
        rawObj: null,
        formattedKey: 'ALL',
        displayLabel: t('reports.allData', 'All Data')
      }
    ];
    let curr = dayjs(fromDate).startOf('day');
    const end = dayjs(toDate).startOf('day');
    while (curr.isBefore(end) || curr.isSame(end, 'day')) {
      dates.push({
        rawObj: curr,
        formattedKey: curr.format('DD/MM/YYYY'),
        displayLabel: curr.format('DD MMM YYYY')
      });
      curr = curr.add(1, 'day');
    }
    return dates;
  }, [fromDate, toDate, t]);

  const groupedData = useMemo(() => {
    if (!data || data.length === 0) return {};
    const grouped = { ALL: {} };

    data.forEach((ritaseItem, index) => {
      const targetTime =
        ritaseItem.in_time || ritaseItem.startTime || ritaseItem.rawTrip?.in_time || ritaseItem.deepDetails?.[0]?.start_time;
      if (!targetTime) return;

      const dateKey = dayjs(targetTime).format('DD/MM/YYYY');
      const vName =
        ritaseItem.license_plate ||
        ritaseItem.licensePlate ||
        ritaseItem.rawTrip?.license_plate ||
        t('reports.unknownVehicle', 'Unknown Vehicle');

      if (!grouped[dateKey]) grouped[dateKey] = {};
      if (!grouped['ALL']) grouped['ALL'] = {};

      if (!grouped[dateKey][vName]) {
        grouped[dateKey][vName] = {
          vehicleName: vName,
          ritases: []
        };
      }
      if (!grouped['ALL'][vName]) {
        grouped['ALL'][vName] = {
          vehicleName: vName,
          ritases: []
        };
      }

      const rawDetails = Array.isArray(ritaseItem.deepDetails) ? ritaseItem.deepDetails : [];
      let totalDistanceNum = 0;
      let totalDurationNum = 0;

      const mappedDetails = rawDetails.map((visit, vIdx) => {
        const startOdo = Number(visit.start_odo) || 0;
        const endOdo = Number(visit.end_odo) || 0;
        let dist = endOdo - startOdo;
        if (dist < 0) dist = 0;

        const onDur = Number(visit.engine_on_duration) || 0;
        const offDur = Number(visit.engine_off_duration) || 0;
        const idleDur = Number(visit.engine_idle_duration) || 0;
        const totalDur = onDur + offDur + idleDur;

        totalDistanceNum += dist;
        totalDurationNum += totalDur;

        const tripInfo = visit.trip_info;
        let tripDetails = null;
        if (tripInfo) {
          const tripStartTime = new Date(tripInfo.start_time).getTime();
          const tripEndTime = new Date(tripInfo.end_time).getTime();

          if (tripEndTime >= tripStartTime) {
            const tripStartOdo = Number(tripInfo.start_odo) || 0;
            const tripEndOdo = Number(tripInfo.end_odo) || 0;
            let tripDist = tripEndOdo - tripStartOdo;
            if (tripDist < 0) tripDist = 0;

            const tripOnDur = Number(tripInfo.engine_on_duration) || 0;
            const tripOffDur = Number(tripInfo.engine_off_duration) || 0;
            const tripIdleDur = Number(tripInfo.engine_idle_duration) || 0;
            const tripTotalDur = tripOnDur + tripOffDur + tripIdleDur;

            totalDistanceNum += tripDist;
            totalDurationNum += tripTotalDur;

            tripDetails = {
              isTrip: true,
              locationName: tripInfo.spatial_name || t('reports.trip', 'Trip'),
              distanceVal: tripDist,
              distance: `${(tripDist / 1000).toFixed(2)} km`,
              durationVal: tripTotalDur,
              duration: formatDurationSec(tripTotalDur),
              start: { time: formatDate(tripInfo.start_time), odo: (tripStartOdo / 1000).toFixed(2) },
              end: { time: formatDate(tripInfo.end_time), odo: (tripEndOdo / 1000).toFixed(2) },
              activity: {
                engineOn: formatDurationSec(tripOnDur),
                engineOff: formatDurationSec(tripOffDur),
                idle: formatDurationSec(tripIdleDur)
              },
              visitation: tripInfo.visitation || []
            };
          }
        }

        return {
          step: vIdx + 1,
          locationName: visit.spatial_name || t('reports.unknownLocation', 'Unknown Location'),
          distanceVal: dist,
          distance: `${(dist / 1000).toFixed(2)} km`,
          durationVal: totalDur,
          duration: formatDurationSec(totalDur),
          start: { time: formatDate(visit.start_time), odo: (startOdo / 1000).toFixed(2) },
          end: { time: formatDate(visit.end_time), odo: (endOdo / 1000).toFixed(2) },
          activity: {
            engineOn: formatDurationSec(onDur),
            engineOff: formatDurationSec(offDur),
            idle: formatDurationSec(idleDur)
          },
          tripInfo: tripDetails
        };
      });

      const currentRitaseNumberDate = grouped[dateKey][vName].ritases.length + 1;
      const currentRitaseNumberAll = grouped['ALL'][vName].ritases.length + 1;

      const ritaseDataForDate = {
        id: `ritase-${ritaseItem.vehicle_id}-${index}`,
        title: `${t('reportModule.ritaseUpper', 'RITASE')} ${currentRitaseNumberDate}`,
        isActiveRoute: ritaseItem.isActiveRoute,
        totalDistance: `${(totalDistanceNum / 1000).toFixed(2)} km`,
        totalDuration: formatDurationSec(totalDurationNum),
        rawTotalDistance: totalDistanceNum,
        rawTotalDuration: totalDurationNum,
        stops: mappedDetails.length,
        details: mappedDetails
      };

      const ritaseDataForAll = {
        ...ritaseDataForDate,
        title: `${t('reportModule.ritaseUpper', 'RITASE')} ${currentRitaseNumberAll}`,
        id: `ritase-all-${ritaseItem.vehicle_id}-${index}`
      };

      grouped[dateKey][vName].ritases.push(ritaseDataForDate);
      grouped['ALL'][vName].ritases.push(ritaseDataForAll);
    });

    return grouped;
  }, [data, t]);

  const handleExport = async (type) => {
    handleExportClose();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('reportModule.ritaseReport', 'Ritase Report'));

    worksheet.columns = [
      { header: t('general.date', 'Date'), key: 'date', width: 15 },
      { header: t('reportFatigue.vehicleCol', 'Vehicle'), key: 'vehicle', width: 15 },
      { header: t('reports.ritase', 'Ritase'), key: 'ritase', width: 15 },
      { header: t('reportModule.step', 'Step'), key: 'step', width: 10 },
      { header: t('general.type', 'Type'), key: 'type', width: 15 },
      { header: t('general.location', 'Location'), key: 'location', width: 35 },
      { header: t('achievement.startTime', 'Start Time'), key: 'startTime', width: 20 },
      { header: t('achievement.endTime', 'End Time'), key: 'endTime', width: 20 },
      { header: t('reportModule.startOdo', 'Start Odo'), key: 'startOdo', width: 15 },
      { header: t('reportModule.endOdo', 'End Odo'), key: 'endOdo', width: 15 },
      { header: t('reportModule.distanceKm', 'Distance (km)'), key: 'distance', width: 15 },
      { header: t('reports.duration', 'Duration'), key: 'duration', width: 15 },
      { header: t('reports.engineOn', 'Engine On'), key: 'engineOn', width: 15 },
      { header: t('reports.engineOff', 'Engine Off'), key: 'engineOff', width: 15 },
      { header: t('reportModule.idle', 'Idle'), key: 'idle', width: 15 }
    ];

    let currentRow = 2;
    const merges = [];

    let dateStartRow = 2;
    let vehicleStartRow = 2;
    let ritaseStartRow = 2;

    let currentLastDate = null;
    let currentLastVehicle = null;

    Object.keys(groupedData).forEach((date) => {
      if (date === 'ALL') return;
      Object.keys(groupedData[date]).forEach((vehicle) => {
        const vehicleData = groupedData[date][vehicle];
        vehicleData.ritases.forEach((ritase, rIdx) => {
          ritase.details.forEach((detail, idx) => {
            const isFirstDetail = idx === 0;

            let outDate = '';
            let outVehicle = '';
            let outRitase = '';

            if (isFirstDetail) {
              if (date !== currentLastDate) {
                if (currentRow > 2) {
                  merges.push([dateStartRow, 1, currentRow - 1, 1]);
                  merges.push([vehicleStartRow, 2, currentRow - 1, 2]);
                  merges.push([ritaseStartRow, 3, currentRow - 1, 3]);
                }
                dateStartRow = currentRow;
                vehicleStartRow = currentRow;
                ritaseStartRow = currentRow;
                outDate = date;
                currentLastDate = date;
                outVehicle = vehicle;
                currentLastVehicle = vehicle;
                outRitase = ritase.title;
              } else if (vehicle !== currentLastVehicle) {
                merges.push([vehicleStartRow, 2, currentRow - 1, 2]);
                merges.push([ritaseStartRow, 3, currentRow - 1, 3]);
                vehicleStartRow = currentRow;
                ritaseStartRow = currentRow;
                outVehicle = vehicle;
                currentLastVehicle = vehicle;
                outRitase = ritase.title;
              } else {
                merges.push([ritaseStartRow, 3, currentRow - 1, 3]);
                ritaseStartRow = currentRow;
                outRitase = ritase.title;
              }
            }

            worksheet.addRow({
              date: outDate,
              vehicle: outVehicle,
              ritase: outRitase,
              step: detail.step,
              type: 'Enter Polygon',
              location: detail.locationName,
              startTime: detail.start?.time || '',
              endTime: detail.end?.time || '',
              startOdo: detail.start?.odo || 0,
              endOdo: detail.end?.odo || 0,
              distance: parseFloat(((detail.distanceVal || 0) / 1000).toFixed(2)),
              duration: detail.duration || 0,
              engineOn: detail.activity?.engineOn || 0,
              engineOff: detail.activity?.engineOff || 0,
              idle: detail.activity?.idle || 0
            });
            currentRow++;

            if (detail.tripInfo) {
              const trip = detail.tripInfo;
              let tripLocation = trip.locationName;
              if (idx === ritase.details.length - 1 && rIdx < vehicleData.ritases.length - 1) {
                const nextTitle = vehicleData.ritases[rIdx + 1].title;
                const match = nextTitle.match(/\d+/);
                const num = match ? match[0] : '';
                tripLocation += num ? ` ~ ${t('reportModule.ritaseLabel', 'Ritase')} ${num}` : ` ~ ${nextTitle}`;
              }

              worksheet.addRow({
                date: '',
                vehicle: '',
                ritase: '',
                step: '',
                type: 'On Trip',
                location: tripLocation,
                startTime: trip.start?.time || '',
                endTime: trip.end?.time || '',
                startOdo: trip.start?.odo || 0,
                endOdo: trip.end?.odo || 0,
                distance: parseFloat(((trip.distanceVal || 0) / 1000).toFixed(2)),
                duration: trip.duration || 0,
                engineOn: trip.activity?.engineOn || 0,
                engineOff: trip.activity?.engineOff || 0,
                idle: trip.activity?.idle || 0
              });
              currentRow++;
            }
          });
        });
      });
    });

    if (currentRow > 2) {
      merges.push([dateStartRow, 1, currentRow - 1, 1]);
      merges.push([vehicleStartRow, 2, currentRow - 1, 2]);
      merges.push([ritaseStartRow, 3, currentRow - 1, 3]);
    }

    if (type === 'excel') {
      merges.forEach(([r1, c1, r2, c2]) => {
        if (r1 < r2) {
          worksheet.mergeCells(r1, c1, r2, c2);
        }
      });
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (colNumber <= 15) {
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
              left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
              bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
              right: { style: 'thin', color: { argb: 'FFBFBFBF' } }
            };
          }
        });
      });
      worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
    }
    const timestamp = dayjs().format('YYYYMMDD_HHmmss');
    const userName = localStorage.getItem('name') || 'User';
    const baseFilename = `Report-Ritase-${timestamp}-by-${userName}`;

    if (type === 'excel') {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseFilename}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else if (type === 'csv') {
      const buffer = await workbook.csv.writeBuffer();
      const blob = new Blob([buffer], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseFilename}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  if (loading)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6 }}>
        {loadingProgress > 0 ? (
          <Box sx={{ width: '60%', mx: 'auto', mt: 2, mb: 2, textAlign: 'center' }}>
            <LinearProgress
              variant="determinate"
              value={loadingProgress}
              sx={{
                height: 10,
                borderRadius: 5,
                mb: 1.5,
                bgcolor: 'rgba(72, 93, 154, 0.2)',
                '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
              }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              {loadingProgress}% - {loadingText}
            </Typography>
          </Box>
        ) : (
          <CircularProgress thickness={4} sx={{ color: '#1e3a8a' }} />
        )}
      </Box>
    );

  if (dateTabs.length === 0)
    return (
      <Paper sx={{ p: 4, textAlign: 'center', mt: 3, borderRadius: 3, border: '1px dashed #cbd5e1', bgcolor: '#f8fafc' }}>
        <Typography color="text.secondary" fontWeight="500">
          {t('reports.selectDateFromFilter', 'Pilih tanggal dari filter di atas lalu klik Generate.')}
        </Typography>
      </Paper>
    );

  const currentTabDateKey = dateTabs[activeTab]?.formattedKey;
  const vehiclesOnCurrentDate = groupedData[currentTabDateKey] || {};
  const vehicleNames = Object.keys(vehiclesOnCurrentDate);

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid #e2e8f0', pb: 1 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { fontWeight: '700', color: '#64748b', textTransform: 'none', fontSize: '0.95rem' },
            '& .Mui-selected': { backgroundColor: '#cfe3ff', color: '#1e3a8a !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#1e3a8a', height: 3, borderRadius: '3px 3px 0 0' }
          }}
        >
          {dateTabs.map((tab, idx) => {
            const dateKey = tab.formattedKey;
            const vehiclesOnDate = groupedData[dateKey] || {};
            let totalDataCount = 0;
            Object.values(vehiclesOnDate).forEach((vehicleData) => {
              totalDataCount += vehicleData.ritases.length;
            });

            return (
              <Tab
                key={idx}
                label={
                  <Badge
                    badgeContent={totalDataCount}
                    sx={{
                      '& .MuiBadge-badge': { backgroundColor: '#485D9A', color: 'white' },
                      pr: totalDataCount > 0 ? 2 : 0
                    }}
                  >
                    {tab.displayLabel}
                  </Badge>
                }
              />
            );
          })}
        </Tabs>
        <Box>
          <Button
            variant="contained"
            onClick={handleExportClick}
            startIcon={<DownloadIcon />}
            sx={{
              bgcolor: '#1e8a61',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(30, 58, 138, 0.2)',
              '&:hover': { bgcolor: '#1eaf7f' }
            }}
          >
            {t('general.export', 'Export')}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleExportClose}
            PaperProps={{
              sx: { mt: 1, borderRadius: 2, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }
            }}
          >
            <MenuItem onClick={() => handleExport('excel')} sx={{ fontWeight: 500, fontSize: '0.9rem', px: 3 }}>
              {t('notification.exportToExcel', 'Export to Excel')}
            </MenuItem>
            <MenuItem onClick={() => handleExport('csv')} sx={{ fontWeight: 500, fontSize: '0.9rem', px: 3 }}>
              {t('notification.exportToCsv', 'Export to CSV')}
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <Box>
        {vehicleNames.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 3, border: '2px dashed #e2e8f0' }}>
            <LocalShippingIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
            <Typography variant="h6" color="#475569" fontWeight="600" mb={1}>
              {t('reports.noOperationalData', 'Tidak ada data operasional')}
            </Typography>
            <Typography variant="body2" color="#64748b">
              {t('reports.noRitaseNote', 'Kendaraan belum memiliki catatan ritase pada tanggal ini.')}
            </Typography>
          </Paper>
        ) : (
          vehicleNames.map((vName) => <VehicleAccordionItem key={vName} vehicleName={vName} vehicleData={vehiclesOnCurrentDate[vName]} />)
        )}
      </Box>
    </Box>
  );
};
export default RitaseDetailReportSort;
